export type SolverProfileId = 'ausgewogen' | 'wenigeTage' | 'kompakteTage' | 'vieleTage';
export type FavTimesLevel = 'ignorieren' | 'wenig' | 'mittel' | 'stark';

/** Strukturelle Solver-Gewichte eines Profils (ohne weight_preferred_times).
 * Bewusst type statt interface: Typ-Aliase erhalten die implizite
 * Index-Signatur und bleiben so Record<string, number>-kompatibel. */
export type SolverWeights = {
  weight_active_days: number;
  weight_unscheduled: number;
  weight_num_blocks: number;
  weight_gap_time: number;
  weight_total_span: number;
  weight_balance: number;
};

/** Vollständiges advancedWeights-Objekt, wie es ans Backend geht. */
export type AdvancedWeights = SolverWeights & {
  weight_preferred_times: number;
};

export interface SolverProfile {
  id: SolverProfileId;
  label: string;
  shortDesc: string;
  tradeoffMain: string;
  tradeoffSub: string | null;
  weights: SolverWeights;
}

export const SOLVER_PROFILES: Record<SolverProfileId, SolverProfile> = {
  ausgewogen: {
    id: 'ausgewogen',
    label: 'Ausgewogen',
    shortDesc: 'Gute Standardlösung ohne starken Einzelfokus.',
    tradeoffMain: 'Gute Standardlösung. Unterrichtstage, Tagesstruktur und Schülerwünsche werden gleichmäßig berücksichtigt.',
    tradeoffSub: null,
    weights: {
      weight_active_days: 1000,
      weight_unscheduled: 100000,
      weight_num_blocks: 500,
      weight_gap_time: 100,
      weight_total_span: 1,
      weight_balance: 1000,
    },
  },
  wenigeTage: {
    id: 'wenigeTage',
    label: 'Wenige Tage',
    shortDesc: 'Unterricht auf möglichst wenige Tage bündeln.',
    tradeoffMain: 'Unterricht wird auf möglichst wenige Tage gebündelt.',
    tradeoffSub: 'Einzelne Tage können dafür voller oder unruhiger werden.',
    weights: {
      weight_active_days: 12000,
      weight_unscheduled: 100000,
      weight_num_blocks: 150,
      weight_gap_time: 20,
      weight_total_span: 1,
      weight_balance: 0,
    },
  },
  kompakteTage: {
    id: 'kompakteTage',
    label: 'Kompakte Tage',
    shortDesc: 'Lücken und Wartezeiten im Tagesverlauf minimieren.',
    tradeoffMain: 'Lücken und lange Wartezeiten im Tagesverlauf werden reduziert.',
    tradeoffSub: 'Dafür können mehr Unterrichtstage nötig sein.',
    weights: {
      weight_active_days: 300,
      weight_unscheduled: 100000,
      weight_num_blocks: 4000,
      weight_gap_time: 800,
      weight_total_span: 50,
      weight_balance: 0,
    },
  },
  vieleTage: {
    id: 'vieleTage',
    label: 'Viele Tage',
    shortDesc: 'Unterricht auf möglichst viele Tage verteilen.',
    tradeoffMain: 'Unterricht wird gleichmäßig auf möglichst viele Wochentage verteilt.',
    tradeoffSub: 'Einzelne Tage haben dafür weniger Stunden.',
    weights: {
      weight_active_days: -30000,
      weight_unscheduled: 100000,
      weight_num_blocks: 500,
      weight_gap_time: 100,
      weight_total_span: 1,
      weight_balance: 5000,
    },
  },
};

export const PROFILE_ORDER: SolverProfileId[] = ['ausgewogen', 'wenigeTage', 'kompakteTage', 'vieleTage'];

/** Separate dimension: how strongly student favorite times are weighted. */
export const FAV_TIMES_OPTIONS: Record<FavTimesLevel, { id: FavTimesLevel; label: string; value: number }> = {
  ignorieren: { id: 'ignorieren', label: 'Ignorieren', value: 0 },
  wenig: { id: 'wenig', label: 'Wenig', value: 200 },
  mittel: { id: 'mittel', label: 'Mittel', value: 800 },
  stark: { id: 'stark', label: 'Stark', value: 3000 },
};
export const FAV_TIMES_ORDER: FavTimesLevel[] = ['ignorieren', 'wenig', 'mittel', 'stark'];
export const FAV_TIMES_DEFAULT: FavTimesLevel = 'mittel';

/**
 * Builds the full advancedWeights object from a profile + favTimesLevel.
 * Unbekannte IDs fallen auf 'ausgewogen' bzw. 'mittel' zurück (die Werte kommen
 * teils aus localStorage und können veraltet sein).
 */
export function buildWeights(profileId: string, favTimesLevel: string): AdvancedWeights {
  const profile = (SOLVER_PROFILES as Record<string, SolverProfile>)[profileId] || SOLVER_PROFILES.ausgewogen;
  const favOptions = FAV_TIMES_OPTIONS as Record<string, { value: number }>;
  const favWeight = (favOptions[favTimesLevel] || FAV_TIMES_OPTIONS.mittel).value;
  return { ...profile.weights, weight_preferred_times: favWeight };
}

/**
 * Returns true if the structural weights (excluding weight_preferred_times)
 * match the given profile. Used to detect manual "angepasst" overrides.
 */
export function profilesMatch(weights: Record<string, unknown> | null | undefined, profileId: string): boolean {
  const profile = (SOLVER_PROFILES as Record<string, SolverProfile>)[profileId];
  if (!weights || !profileId || !profile) return false;
  const profileWeights: Record<string, number> = { ...profile.weights };
  return Object.keys(profileWeights).every(k => Number(weights[k]) === Number(profileWeights[k]));
}
